import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../components/common/Button';
import ModernGallery from '../components/gallery/ModernGallery';
import CountdownTimer from '../components/countdown/CountdownTimer';
import HomepageBlog from '../components/blog/HomepageBlog';
import AlumniCarousel from '../components/alumni/AlumniCarousel';
import AlumniShowcase from '../components/AlumniShowcase/AlumniShowcase';
import api from '../services/api';

const HomePage = () => {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const [stats, setStats] = useState({
    totalGraduates: 0,
    totalDepartments: 0,
    totalCourses: 0
  });
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(null);
  const [alumniCount, setAlumniCount] = useState(0);
  const [initialLoad, setInitialLoad] = useState(true);

  const allGalleryImages = [
    { id: 1, src: '/images/1.jpg', title: '2026 1st Graduation Ceremony' },
    { id: 2, src: '/images/2.jpg', title: '2026 1st Graduation Ceremony' },
    { id: 3, src: '/images/3.jpg', title: '2026 1st Graduation Ceremony' },
    { id: 4, src: '/images/4.jpg', title: '2026 1st Graduation Ceremony' },
    { id: 5, src: '/images/5.jpg', title: '2026 1st Graduation Ceremony' },
    { id: 6, src: '/images/6.jpg', title: '2026 1st Graduation Ceremony' },
    { id: 7, src: '/images/7.jpg', title: '2026 1st Graduation Ceremony' },
    { id: 8, src: '/images/8.jpg', title: '2026 1st Graduation Ceremony' },
    { id: 9, src: '/images/9.jpg', title: '2026 1st Graduation Ceremony' },
    { id: 10, src: '/images/10.jpg', title: '2026 1st Graduation Ceremony' },
    { id: 11, src: '/images/11.jpg', title: '2026 1st Graduation Ceremony' },
    { id: 12, src: '/images/12.jpg', title: '2026 1st Graduation Ceremony' },
    { id: 13, src: '/images/13.jpg', title: '2026 1st Graduation Ceremony' },
    { id: 14, src: '/images/14.jpg', title: '2026 1st Graduation Ceremony' },
    { id: 15, src: '/images/15.jpg', title: '2026 1st Graduation Ceremony' },
    { id: 16, src: '/images/16.jpg', title: '2026 1st Graduation Ceremony' },
    { id: 17, src: '/images/17.jpg', title: '2026 1st Graduation Ceremony' },
    { id: 18, src: '/images/18.jpg', title: '2026 1st Graduation Ceremony' }
  ];

  useEffect(() => {
    // Scroll to hero section on initial load
    if (initialLoad && heroRef.current) {
      setTimeout(() => {
        heroRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setInitialLoad(false);
      }, 500);
    }

    fetchStatistics();
    fetchCountdown();
    fetchAlumniCount();
  }, []);

  const fetchStatistics = async () => {
    try {
      const [graduatesRes, departmentsRes, coursesRes] = await Promise.all([
        api.get('/graduates'),
        api.get('/departments'),
        api.get('/courses')
      ]);

      setStats({
        totalGraduates: graduatesRes.data.length,
        totalDepartments: departmentsRes.data.length,
        totalCourses: coursesRes.data.length
      });
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCountdown = async () => {
    try {
      console.log('🔄 Fetching countdown...');
      const response = await api.get('/countdown');
      console.log('📡 Countdown API Response:', response.data);

      if (response.data && response.data.is_active === true) {
        console.log('✅ Countdown is ACTIVE');
        setCountdown(response.data);
      } else {
        console.log('❌ Countdown is NOT active');
      }
    } catch (error) {
      console.error('Error fetching countdown:', error);
    }
  };

  const fetchAlumniCount = async () => {
    try {
      const response = await api.get('/alumni/total-count');
      if (response.data.success) {
        setAlumniCount(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching alumni count:', error);
    }
  };

  return (
    <div>
      {/* Loading Overlay */}
      {initialLoad && (
        <div className="fixed inset-0 bg-primary z-50 flex items-center justify-center animate-fade-out">
          <div className="text-center">
            <div className="w-20 h-20 border-4 border-white/20 border-t-white rounded-full animate-spin mb-4"></div>
            <h2 className="text-white text-xl font-bold">Welcome to Katine TTI</h2>
            <p className="text-white/80 text-sm mt-2">Loading...</p>
          </div>
        </div>
      )}

      {/* Hero Section with ref */}
      <div ref={heroRef} className="relative h-[450px] sm:h-[500px] md:h-[550px] overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute top-0 left-0 w-full h-full object-cover"
          poster="/images/hero-poster.jpg"
        >
          <source src="/videos/katine.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-transparent via-white/5 to-transparent"></div>
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-white text-center px-4">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3 drop-shadow-lg animate-fade-in">
            Welcome to Katine TTI
          </h1>
          <p className="text-sm sm:text-base md:text-lg mb-4 sm:mb-6 max-w-2xl drop-shadow-md px-2 animate-fade-in-up">
            Verify graduation status, search through our alumni database, and celebrate academic achievements
          </p>
          <div className="flex flex-row justify-center gap-3 w-auto px-4 animate-fade-in-up delay-200">
            <Link to="/search">
              <button className="px-5 py-2.5 text-sm sm:text-base font-semibold rounded-lg bg-[#f4ba24] border-2 border-primary text-primary hover:bg-[#e6a812] shadow-lg whitespace-nowrap inline-flex items-center justify-center transition-all duration-300 hover:scale-105" style={{ height: '44px' }}>
                <span className="sm:hidden">Search Now</span>
                <span className="hidden sm:inline">Search Graduates Now</span>
              </button>
            </Link>
            <Link to="/gown-request">
              <button className="px-5 py-2.5 text-sm sm:text-base font-semibold rounded-lg bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary shadow-lg whitespace-nowrap inline-flex items-center justify-center transition-all duration-300 hover:scale-105" style={{ height: '44px' }}>
                Request Gown
              </button>
            </Link>
          </div>
          <div className="absolute bottom-6 sm:bottom-8 md:bottom-12 left-0 right-0 animate-fade-in-up delay-300">
            <div className="container mx-auto px-4">
              <div className="flex justify-center gap-8 sm:gap-12 md:gap-16 lg:gap-24">
                <div className="text-center">
                  <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-secondary drop-shadow-lg">
                    {loading ? '...' : stats.totalGraduates}+
                  </p>
                  <p className="text-[10px] sm:text-xs text-white mt-0.5 sm:mt-1">Total Graduates</p>
                </div>
                <div className="text-center">
                  <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-secondary drop-shadow-lg">
                    {loading ? '...' : stats.totalDepartments}
                  </p>
                  <p className="text-[10px] sm:text-xs text-white mt-0.5 sm:mt-1">Departments</p>
                </div>
                <div className="text-center">
                  <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-secondary drop-shadow-lg">
                    {loading ? '...' : stats.totalCourses}+
                  </p>
                  <p className="text-[10px] sm:text-xs text-white mt-0.5 sm:mt-1">Courses</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Countdown Timer Section */}
      {countdown && countdown.is_active === true && countdown.ceremony_date && (
        <CountdownTimer
          targetDate={countdown.ceremony_date}
          title={countdown.title}
          message={countdown.message}
        />
      )}

      {/* Blog Section */}
      <HomepageBlog />

      {/* Alumni Association Section (Committee Members) */}
      <section className="py-12 bg-gray-50 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="mb-8 animate-fade-in-up">
            <span className="inline-block px-3 py-1 bg-[#fbbf24]/20 text-[#fbbf24] rounded-full text-xs font-semibold mb-3">
              Alumni Network
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
              Alumni Committee
            </h2>
            <p className="text-gray-600 text-sm md:text-base max-w-2xl">
              Meet our distinguished alumni making an impact
            </p>
            <div className="w-16 h-0.5 bg-[#fbbf24] mt-3 rounded-full"></div>
          </div>

          <AlumniCarousel
            title="Alumni committee"
            subtitle="Meet our distinguished alumni making an impact"
            autoScroll={true}
            scrollSpeed={0.8}
            limit={8}
          />

          <div className="text-center mt-8 animate-fade-in-up delay-200">
            <Button
              onClick={() => navigate('/alumni')}
              variant="outline"
              className="px-6 py-2 transition-all duration-300 hover:scale-105"
            >
              View All Alumni →
            </Button>
          </div>
        </div>
      </section>

      {/* Gallery Section - Left Aligned */}
      <div className="py-12" style={{ backgroundColor: '#FEFBEC' }}>
        <div className="container mx-auto px-4">
          <div className="mb-8 animate-fade-in-up">
            <span className="inline-block px-3 py-1 bg-[#fbbf24]/20 text-[#fbbf24] rounded-full text-xs font-semibold mb-3">
              Memories
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
              Gallery
            </h2>
            <p className="text-gray-600 text-sm md:text-base max-w-2xl">
              Relive the special moments from our past ceremonies, visits and events.
            </p>
            <div className="w-16 h-0.5 bg-[#fbbf24] mt-3 rounded-full"></div>
          </div>

          <ModernGallery images={allGalleryImages} />
        </div>
      </div>

      {/* Registered Alumni Showcase Section with Join Button */}
      <div
        className="relative min-h-[500px] overflow-hidden"
        style={{
          backgroundImage: "url('/images/community.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>

        <div className="relative z-10 py-16">
          <div className="container mx-auto px-4">
            <div className="mb-10 animate-slide-in-left">
              <span className="inline-block px-3 py-1 bg-[#fbbf24]/20 text-[#fbbf24] rounded-full text-xs font-semibold mb-3">
                Alumni Community
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Our Alumni Family
              </h2>
              <p className="text-white/80 text-sm md:text-base">
                Join our growing community of <span className="font-semibold text-[#fbbf24]">{alumniCount}+</span> registered alumni
              </p>
              <div className="w-16 h-0.5 bg-[#fbbf24] mt-3 rounded-full animate-grow-width"></div>
            </div>

            <div className="animate-fade-in-up delay-300">
              <AlumniShowcase />
            </div>

            {/* Join Alumni Button - Moved further down with more top margin */}
            <div className="flex justify-center mt-24 mb-4 animate-fade-in-up delay-500">
              <Link to="/alumni/register">
                <button className="px-6 py-2 text-base font-semibold rounded-lg bg-transparent border-2 border-[#fbbf24] text-[#fbbf24] hover:bg-[#fbbf24]/10 transition-all duration-300 hover:scale-105 flex items-center gap-2 shadow-md">
                  <span>🎓</span>
                  Join Alumni
                  <span>→</span>
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slide-in-left {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes grow-width {
          from { width: 0; }
          to { width: 64px; }
        }

        @keyframes fade-out {
          from { opacity: 1; visibility: visible; }
          to { opacity: 0; visibility: hidden; }
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
          opacity: 0;
        }

        .animate-slide-in-left {
          animation: slide-in-left 0.8s ease-out;
        }

        .animate-grow-width {
          animation: grow-width 0.8s ease-out;
        }

        .animate-fade-out {
          animation: fade-out 0.5s ease-out forwards;
        }

        .delay-200 {
          animation-delay: 0.2s;
        }

        .delay-300 {
          animation-delay: 0.3s;
        }

        .delay-500 {
          animation-delay: 0.5s;
        }
      `}</style>
    </div>
  );
};

export default HomePage;
